import { useCallback } from 'react';

import type { ContextResolver } from '@atlassian/app-recommendations-kit/common/types';
import useRecommendations from '@atlassian/app-recommendations-kit/use-recommendations';

const MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE = 'crossJoinNudgeFlags';

export class RecommendationError extends Error {
	options: { cause: Error };

	constructor(message: string, options: { cause: Error }) {
		super(message);
		this.name = 'RecommendationError';
		this.options = options;
	}
}

export function useMSRNudgeRecommendations({
	product,
	sessionId,
	cloudId,
	subProduct,
}: {
	cloudId: string;
	product: string;
	sessionId: string;
	subProduct: string;
}): () => Promise<string[]> {
	const contextResolver: ContextResolver = useCallback(
		() => ({
			product,
			useCase: MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE,
			tenantId: cloudId,
			subproduct: subProduct,
			sessionId,
		}),
		[cloudId, product, subProduct, sessionId],
	);

	const { getRecommendations } = useRecommendations({ contextResolver });

	return useCallback(async (): Promise<string[]> => {
		try {
			const result = await getRecommendations({
				first: 4,
				config: {
					cacheConfig: {
						ttl: 86400, // cache for 1 day in seconds
					},
				},
			});

			const { response: { data } = {} } = result;

			const recommendedEntities = (data ?? []).map((e) => e.entityId);

			return recommendedEntities;
		} catch (error: any) {
			let cause;
			if (error instanceof Error) {
				cause = error;
			} else if (error && error.reason) {
				cause = new Error(error.reason);
			} else {
				cause = new Error(String(error));
			}

			throw new RecommendationError('Get MSR nudge recommendations error', {
				cause,
			});
		}
	}, [getRecommendations]);
}
